// Contract Internal Signing Page — for tenant signatory users
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle2, FileText, PenLine, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import SignaturePad from '../../components/shared/SignaturePad';

export default function ContractSignPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signaturePng, setSignaturePng] = useState(null);
  const [signatoryName, setSignatoryName] = useState('');
  const [signDate, setSignDate] = useState(new Date().toISOString().split('T')[0]);
  const [sigMode, setSigMode] = useState('draw');
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = useRef(null);

  const { data: docData, isLoading: loading } = useQuery({
    queryKey: QK.contractDoc(id),
    queryFn: async () => {
      let fileId = null, isSaleAuth = false;
      try {
        const res = await api.get(`/consignment-contracts/preview-data/${id}`);
        fileId = res.data?.contract_file_id;
      } catch {
        const deliv = await api.get(`/sale-owner-deliverables/${id}`).catch(() => null);
        fileId = deliv?.data?.authorization_file_id;
        isSaleAuth = true;
      }
      let previewUrl = null;
      if (fileId) {
        const token = localStorage.getItem('token');
        const fr = await fetch(`${window.location.origin}/api/files/download/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fr.ok) previewUrl = URL.createObjectURL(await fr.blob());
      }
      return { previewUrl, docType: isSaleAuth ? 'sale_authorization' : 'consignment_contract' };
    },
    enabled: !!id,
  });
  const previewUrl = docData?.previewUrl ?? null;
  const docType    = docData?.docType    ?? 'consignment_contract';

  const handleSign = async () => {
    if (!signaturePng && !signatoryName.trim()) { toast.error('Please draw/upload your signature or enter your name'); return; }
    setSigning(true);
    try {
      const endpoint = docType === 'sale_authorization'
        ? `/sale-authorizations/internal-sign/${id}`
        : `/consignment-contracts/internal-sign/${id}`;
      await api.post(endpoint, {
        signature_png: signaturePng,
        signatory_name: signatoryName,
        date: new Date(signDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      });
      toast.success(docType === 'sale_authorization' ? 'Authorization signed successfully' : 'Contract signed successfully');
      setSigned(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    } finally { setSigning(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setSignaturePng(ev.target.result); setUploadPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleModeSwitch = (mode) => {
    setSigMode(mode);
    setSignaturePng(null);
    setUploadPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasSignature = Boolean(signaturePng) || Boolean(signatoryName.trim());

  if (signed) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{docType === 'sale_authorization' ? 'Authorization Signed' : 'Contract Signed'}</h2>
          <p className="text-muted-foreground text-sm mb-6">{docType === 'sale_authorization' ? 'The Sale Authorization has been executed.' : 'Your signature has been recorded. The contract is now ready to send to the owner.'}</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary/90 text-white">
            Return to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <FileText className="w-10 h-10 mx-auto mb-2 text-primary" />
          <h1 className="text-2xl font-bold">{docType === 'sale_authorization' ? 'Sign Sale Authorization' : 'Sign Consignment Contract'}</h1>
          <p className="text-muted-foreground">Please review and sign the document below</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : previewUrl ? (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Contract Document</CardTitle></CardHeader>
            <CardContent className="p-0">
              <object data={previewUrl} type="application/pdf" className="w-full rounded-b" style={{ height: 400 }}>
                <p className="p-4 text-sm text-muted-foreground">Unable to preview PDF.</p>
              </object>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader><CardTitle className="text-base">Signature</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              By signing below you confirm you are authorized to execute this contract on behalf of the consignor.
            </p>

            <div className="flex gap-1 bg-muted rounded-md p-0.5 w-fit" data-testid="signature-mode-toggle">
              <button type="button" onClick={() => handleModeSwitch('draw')} disabled={signing}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${sigMode === 'draw' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <PenLine className="w-3.5 h-3.5" /> Draw
              </button>
              <button type="button" onClick={() => handleModeSwitch('upload')} disabled={signing}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${sigMode === 'upload' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <Upload className="w-3.5 h-3.5" /> Upload
              </button>
            </div>

            {sigMode === 'draw' && (
              <div>
                <Label className="text-xs mb-2 block">Draw your signature *</Label>
                <SignaturePad onChange={setSignaturePng} disabled={signing} />
              </div>
            )}
            {sigMode === 'upload' && (
              <div className="space-y-2">
                <Label className="text-xs mb-2 block">Upload signature image *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded bg-white p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} disabled={signing} />
                  {uploadPreview ? (
                    <div className="space-y-2">
                      <img src={uploadPreview} alt="Uploaded signature" className="max-h-[80px] mx-auto" />
                      <p className="text-xs text-muted-foreground">Click to replace</p>
                    </div>
                  ) : (
                    <div className="py-2">
                      <Upload className="w-6 h-6 mx-auto mb-1.5 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Click to upload a signature image</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Print Name</Label><Input value={signatoryName} onChange={e => setSignatoryName(e.target.value)} placeholder="Full name" disabled={signing} /></div>
              <div><Label className="text-xs">Date</Label><Input type="date" value={signDate} onChange={e => setSignDate(e.target.value)} disabled={signing} /></div>
            </div>
            <Button onClick={handleSign} disabled={signing || !hasSignature} className="w-full bg-primary hover:bg-primary/90 text-white">
              {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Sign Contract</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
